import { useState, useEffect, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useLocation, useSearch } from "wouter";
import { queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Users, DollarSign, Loader2, Star, Building2, Palette, Activity, FileText, FileSignature, Percent, Plug, MapPin, ShieldCheck, Trophy, Lock, Sparkles, Mail, Bell, ShieldAlert, Shield, Globe, Layers, ClipboardCheck, Phone, Coins, Bot, BarChart2, CalendarDays, TrendingUp, ShoppingCart, ArrowRight, AlertCircle, PhoneCall, Heart, Zap } from "lucide-react";
import { AdminDialerSettingsTab } from "@/components/admin/admin-dialer-settings";
import TalentPortalSettings from "@/components/admin/talent-portal-settings";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ApprovalPoliciesPanel, AdminSeatRequestsPanel, ApprovalsInbox } from "@/components/approvals/approvals-shared";
import { AISettingsCard } from "@/components/admin/ai-settings-card";
import { VoiceAssistantSettingsCard } from "@/components/voice-assistant/voice-assistant-settings-card";
import { TourPreferencesCard } from "@/components/voice-assistant/tour-preferences-card";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { UsersAccessDialog } from "@/components/users-access-dialog";
import { ManageTalentDialog } from "@/components/manage-talent-dialog";
import { ManageClientTypesDialog } from "@/components/manage-client-types-dialog";
import { ManageTalentTypesDialog } from "@/components/manage-talent-types-dialog";
import { CollaboratorRolesManager } from "@/components/manage-collaborator-roles";
import { BrandingSettingsDialog } from "@/components/branding-settings-dialog";
import { ProfileSettingsDialog } from "@/components/profile-settings-dialog";
import { TeamActivityDialog } from "@/components/team-activity-dialog";
import TeamActivityPage from "@/pages/team-activity";
// PaymentPortalDialog is intentionally not imported here anymore. The Stripe
// and PayPal connector cards live under Admin → Integrations (Overview +
// Workspace Services tabs), and the Payment Options toggles (Enable Invoice
// Payments, Enable Deposit Collection) now live under Admin → Invoice
// Settings → General Settings. Removing the standalone "Payment Portal"
// admin entry keeps each setting in exactly one place.
import { CommunicationsSettingsDialog } from "@/components/communications-settings-dialog";
import { FormsTemplatesDialog } from "@/components/forms-templates-dialog";
import { ManageCommissionsDialog } from "@/components/manage-commissions-dialog";
import { IntegrationsDialog } from "@/components/integrations-dialog";
import { CalendarSettingsDialog } from "@/components/calendar-settings-dialog";
import { EmailSettingsDialog } from "@/components/email-settings-dialog";
import { RegionAccessSettingsDialog } from "@/components/region-access-settings-dialog";
import { TutorialHint } from "@/components/tutorial-hint";
import { GamificationSettingsDialog } from "@/components/gamification-settings-dialog";
import { SecurityTrainingSettingsDialog } from "@/components/security-training-settings-dialog";
import { TrainingStatusPanel } from "@/components/admin/training-status-panel";
import { AdminSecuritySettingsCard } from "@/components/admin/security-settings-card";
import { SecurityEventsPanel } from "@/components/admin/security-events-panel";
import { DocumentSecurityPanel } from "@/components/admin/document-security-panel";
import { NotificationDefaultsCard } from "@/components/admin/notification-defaults-card";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { apiRequest } from "@/lib/queryClient";
import { format } from "date-fns";
import { AdminLeadSettingsCard } from "@/components/admin/lead-settings-card";
import { CreditDistributionCard } from "@/components/admin/credit-distribution-card";
import { BookingSettingsCard, AiCostRoutingCard } from "@/components/admin/booking-settings-card";
import { CancellationPoliciesCard } from "@/components/admin/cancellation-policies-card";
import { InvoicePoliciesPanel } from "@/components/admin/invoice-policies-panel";
import { EmailAutomationsPanel } from "@/components/admin/email-automations-panel";
import { TalentBillingRollupCard } from "@/components/admin/talent-billing-rollup-card";
import { AiAutoReloadCard } from "@/components/admin/ai-auto-reload-card";
import { LeadAutoTopUpCard } from "@/components/admin/lead-auto-top-up-card";
// Seed-failure UI (banner + notification history) was previously rendered
// above every admin settings panel. It's a platform-ops diagnostic — not
// per-tenant config — so it now lives only on /platform/alerts in the
// super-admin nav and isn't shown to workspace admins at all.

import { useIsMobile } from "@/hooks/use-mobile";
import type { UserProfile } from "@shared/schema";

const menuGroups = [
  {
    group: "Workspace",
    items: [
      { key: "workspaceSettings", label: "Workspace", description: "Client types, branding, and notification defaults", icon: Building2 },
      { key: "users", label: "Users & Permissions", description: "Manage workspace users, roles, and access permissions", icon: Users },
      { key: "security", label: "Security", description: "Security settings, MFA enforcement, training, audit events, document access log, and virus-scanner health & alerts — all in one place", icon: ShieldCheck },
      { key: "integrations", label: "Integrations", description: "Connect Google Calendar, Drive, Stripe, PayPal, SMS/Twilio, DocuSign, Eventbrite, and more", icon: Plug },
      { key: "ai", label: "AI", description: "Toggle AI drafting, set a hard monthly overage cap, and choose who pays for AI usage", icon: Sparkles },
    ],
  },
  {
    group: "Communication",
    items: [
      { key: "emailSettings", label: "Email", description: "Sender reputation, domain auth, mass email compliance, cold-sending audience policy, and alerts", icon: Mail },
      { key: "calendarSettings", label: "Calendar", description: "Workspace calendar defaults — availability calendars, office hours & display timezone, and event content fields (incl. Rebook prefix) that per-talent 'Use default' overrides inherit from", icon: CalendarDays },
      { key: "automations", label: "Automations", description: "Schedule automated emails before or after event dates — day-of reminders, post-event follow-ups, and delinquent invoice alerts", icon: Zap },
    ],
  },
  {
    group: "Sales & Revenue",
    items: [
      { key: "teamActivity", label: "Team Activity", description: "Monitor team member activity, login events, and productivity metrics", icon: Activity },
      { key: "leadSettings", label: "Leads", description: "Lead column defaults, direction tags, and lead credit distribution", icon: MapPin },
      { key: "sales", label: "Sales", description: "Commissions, goals, and approval thresholds for your sales team", icon: Trophy },
      { key: "invoicePolicies", label: "Invoices", description: "One place for invoice payment options, general invoice settings, bookings & multi-talent toggles, cancellation policies, and late-fee schedules — with a live preview of the effective policy per client type / state", icon: FileText },
      { key: "bookingForms", label: "Documents", description: "Manage document templates and DocuSign integration", icon: FileSignature },
      { key: "dialer", label: "Dialer", description: "Workspace call-recording defaults and per-rep caller-ID numbers", icon: PhoneCall },
    ],
  },
  {
    group: "Talent",
    items: [
      { key: "talent", label: "Manage Talent", description: "Configure talent profiles and link to user accounts", icon: Star },
      { key: "talentPortal", label: "Talent Portal", description: "Configure the talent-facing portal", icon: Globe },
    ],
  },
];

const menuItems = menuGroups.flatMap((group) => group.items);

function AdminCoachPolicyCard() {
  const { toast } = useToast();
  const qc = useQueryClient();
  const { data: aiSettings, isLoading } = useQuery<any>({ queryKey: ["/api/admin/ai-settings"] });

  const mutation = useMutation({
    mutationFn: (coachPersonalBlockPolicy: string) =>
      apiRequest("PATCH", "/api/admin/ai-settings", { coachPersonalBlockPolicy }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["/api/admin/ai-settings"] }),
    onError: () => toast({ title: "Failed to save life coach policy", variant: "destructive" }),
  });

  if (isLoading) {
    return (
      <Card>
        <CardContent className="py-8 flex justify-center">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  const current = aiSettings?.coachPersonalBlockPolicy ?? "avoid";

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
            <Heart className="w-5 h-5 text-primary" />
          </div>
          <div>
            <CardTitle className="text-base">Work / life boundary policy</CardTitle>
            <CardDescription>Workspace-wide floor for how strictly the life coach separates work and personal time. The stricter of this and each user's own setting applies.</CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center justify-between gap-4">
          <div>
            <Label className="text-sm">Personal block policy</Label>
            <p className="text-xs text-muted-foreground mt-0.5 max-w-sm">
              Users can only tighten this boundary for themselves — they cannot loosen it below this workspace floor.
            </p>
          </div>
          <Select
            value={current}
            onValueChange={(v) => mutation.mutate(v)}
            disabled={mutation.isPending}
          >
            <SelectTrigger className="w-52 shrink-0" data-testid="select-coach-personal-block-policy">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="allow">Allow — defer to each user</SelectItem>
              <SelectItem value="avoid">Avoid — minimum flexible (default)</SelectItem>
              <SelectItem value="block">Block — force strict for all</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="rounded-md bg-muted/50 p-3 text-xs text-muted-foreground space-y-1">
          <p><span className="font-medium text-foreground">Allow</span> — each user's personal setting takes full effect.</p>
          <p><span className="font-medium text-foreground">Avoid</span> — users who disabled boundaries are upgraded to "flexible"; the coach notes when scheduling at the boundary.</p>
          <p><span className="font-medium text-foreground">Block</span> — everyone is forced to strict; the coach never proposes work tasks during personal blocks and vice-versa.</p>
        </div>
      </CardContent>
    </Card>
  );
}

function WorkspaceTeamAiProfiles() {
  const { data, isLoading } = useQuery<any[]>({ queryKey: ["/api/admin/ai-user-profiles"] });

  const fmt = (v: any, decimals = 2) =>
    v !== null && v !== undefined ? Number(v).toFixed(decimals) : "—";

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold flex items-center gap-2">
            <Users className="h-4 w-4 text-purple-600" />
            Team AI Tone Profiles
          </CardTitle>
          <CardDescription className="text-xs">
            Read-only. Derived from each user's Coach Mode interactions via EWMA (α=0.2).
            Profiles appear after 3+ samples and are used to style AI replies.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
          {!isLoading && (!data || data.length === 0) && (
            <p className="text-xs text-muted-foreground">
              No tone profiles yet. Profiles build automatically as users interact with Coach Mode.
            </p>
          )}
          {!isLoading && data && data.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b text-left text-muted-foreground">
                    <th className="pb-2 pr-3 font-medium">User</th>
                    <th className="pb-2 pr-3 font-medium">Role</th>
                    <th className="pb-2 pr-3 font-medium">Samples</th>
                    <th className="pb-2 pr-3 font-medium">Avg sentence len</th>
                    <th className="pb-2 pr-3 font-medium">Formality</th>
                    <th className="pb-2 pr-3 font-medium">Vocab diversity</th>
                    <th className="pb-2 pr-3 font-medium">Emoji freq</th>
                    <th className="pb-2 pr-3 font-medium">Faith terms</th>
                    <th className="pb-2 font-medium">Profanity</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {data.map((p: any) => (
                    <tr key={p.userId} data-testid={`row-ai-profile-${p.userId}`} className="hover:bg-muted/40">
                      <td className="py-2 pr-3 font-medium">{p.displayName ?? p.email ?? p.userId.slice(0, 8)}</td>
                      <td className="py-2 pr-3 text-muted-foreground capitalize">{p.role ?? "—"}</td>
                      <td className="py-2 pr-3">{p.sampleCount ?? 0}</td>
                      <td className="py-2 pr-3">{fmt(p.avgSentenceLen)}</td>
                      <td className="py-2 pr-3">{fmt(p.formalityScore)}</td>
                      <td className="py-2 pr-3">{fmt(p.vocabDiversity)}</td>
                      <td className="py-2 pr-3">{fmt(p.emojiFreq)}</td>
                      <td className="py-2 pr-3">
                        {p.faithTermsUsed ? (
                          <span className="text-amber-600 font-medium">Yes</span>
                        ) : (
                          <span className="text-muted-foreground">No</span>
                        )}
                      </td>
                      <td className="py-2">
                        {p.profanityOk ? (
                          <span className="text-red-500 font-medium">Yes</span>
                        ) : (
                          <span className="text-muted-foreground">No</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function WorkspaceAiUsageInsights() {
  const { data, isLoading } = useQuery<{
    summaries: any[];
    totalSessions: number;
    escalatedCount: number;
    windowDays: number;
  }>({ queryKey: ["/api/admin/ai-usage-insights"] });

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground">Sessions (all time)</p>
            {isLoading ? (
              <Loader2 className="h-4 w-4 animate-spin mt-1 text-muted-foreground" />
            ) : (
              <p className="text-2xl font-bold text-blue-600 mt-1">{data?.totalSessions ?? 0}</p>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground">Escalated sessions</p>
            {isLoading ? (
              <Loader2 className="h-4 w-4 animate-spin mt-1 text-muted-foreground" />
            ) : (
              <p className={`text-2xl font-bold mt-1 ${(data?.escalatedCount ?? 0) > 0 ? "text-amber-600" : "text-gray-700"}`}>
                {data?.escalatedCount ?? 0}
              </p>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground">Active team members</p>
            {isLoading ? (
              <Loader2 className="h-4 w-4 animate-spin mt-1 text-muted-foreground" />
            ) : (
              <p className="text-2xl font-bold text-purple-600 mt-1">{data?.summaries?.length ?? 0}</p>
            )}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold flex items-center gap-2">
            <BarChart2 className="h-4 w-4 text-blue-600" />
            Per-User AI Usage
          </CardTitle>
          <CardDescription className="text-xs">
            30-day roll-up computed nightly. Data appears the morning after the first session is ended.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
          {!isLoading && (!data?.summaries || data.summaries.length === 0) && (
            <p className="text-xs text-muted-foreground">
              No data yet. Summaries are computed nightly after your team uses Clarik AI.
            </p>
          )}
          <div className="space-y-2">
            {data?.summaries.map((s: any) => {
              const sum = s.summary ?? {};
              return (
                <div
                  key={s.userId}
                  className="border rounded px-3 py-2 text-xs flex items-center justify-between gap-4"
                  data-testid={`ai-usage-row-${s.userId}`}
                >
                  <span className="font-mono text-muted-foreground">{s.userId.slice(0, 10)}…</span>
                  <span>{sum.sessionCount ?? 0} sessions</span>
                  <span>{sum.avgTurnsPerSession ? `${Number(sum.avgTurnsPerSession).toFixed(1)} turns/session` : "—"}</span>
                  <span>
                    {sum.avgSentimentScore !== null && sum.avgSentimentScore !== undefined ? (
                      <Badge className={`text-xs ${Number(sum.avgSentimentScore) >= 0.6 ? "bg-green-100 text-green-700" : Number(sum.avgSentimentScore) <= 0.35 ? "bg-red-100 text-red-700" : "bg-gray-100 text-gray-600"}`}>
                        {Number(sum.avgSentimentScore) >= 0.6 ? "Positive" : Number(sum.avgSentimentScore) <= 0.35 ? "Needs attention" : "Neutral"}
                      </Badge>
                    ) : "—"}
                  </span>
                  {sum.escalatedCount > 0 && (
                    <Badge className="bg-amber-100 text-amber-700 text-xs">{sum.escalatedCount} escalated</Badge>
                  )}
                  {s.computedAt && (
                    <span className="text-muted-foreground ml-auto shrink-0">
                      Updated {format(new Date(s.computedAt), "MMM d")}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

interface CreditConfigLead {
  mode: string; payer: string; rolloverEnabled: boolean; rolloverCapMultiplier: number;
  overagePolicy: string; negativeLimitCount: number; defaultByRole: Record<string, number>;
  repCanBuyCredits: boolean; repMonthlyCreditPurchaseCap: number | null;
}
interface CreditConfigResponse { ai: any; lead: CreditConfigLead; }

function RepPurchasingCard() {
  const { toast } = useToast();
  const qc = useQueryClient();
  const [repCanBuy, setRepCanBuy] = useState(true);
  const [capValue, setCapValue] = useState("");
  const [dirty, setDirty] = useState(false);

  const { data: cfg, isLoading } = useQuery<CreditConfigResponse>({
    queryKey: ["/api/admin/credit-config"],
  });

  useEffect(() => {
    if (!cfg?.lead) return;
    setRepCanBuy(cfg.lead.repCanBuyCredits ?? true);
    setCapValue(cfg.lead.repMonthlyCreditPurchaseCap != null ? String(cfg.lead.repMonthlyCreditPurchaseCap) : "");
    setDirty(false);
  }, [cfg]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      const cap = capValue.trim() === "" ? null : parseInt(capValue, 10);
      const res = await apiRequest("PATCH", "/api/admin/credit-config", {
        lead: {
          repCanBuyCredits: repCanBuy,
          repMonthlyCreditPurchaseCap: cap && !isNaN(cap) && cap > 0 ? cap : null,
        },
      });
      return res.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/admin/credit-config"] });
      setDirty(false);
      toast({ title: "Rep purchasing settings saved" });
    },
    onError: () => toast({ title: "Failed to save", variant: "destructive" }),
  });

  if (isLoading) return (
    <Card><CardContent className="py-8 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></CardContent></Card>
  );

  return (
    <Card data-testid="card-rep-purchasing">
      <CardHeader>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
            <ShoppingCart className="w-5 h-5 text-primary" />
          </div>
          <div>
            <CardTitle className="text-lg">Rep Purchasing</CardTitle>
            <CardDescription>Control whether reps can buy lead credits independently and set spending limits</CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="flex items-center justify-between rounded-lg border p-4">
          <div>
            <Label className="text-sm font-medium">Allow reps to purchase credits</Label>
            <p className="text-xs text-muted-foreground mt-0.5">
              When off, only admins and billing can buy lead credits. Reps will see a clear message directing them to their admin.
            </p>
          </div>
          <Switch
            checked={repCanBuy}
            onCheckedChange={(v) => { setRepCanBuy(v); setDirty(true); }}
            data-testid="switch-rep-can-buy-credits"
          />
        </div>

        {repCanBuy && (
          <div className="space-y-2">
            <Label className="text-sm font-medium">Monthly purchase cap per rep (credits)</Label>
            <p className="text-xs text-muted-foreground">
              Leave blank for unlimited. When set, a rep who would exceed this cap gets a clear error instead of a silent failure.
            </p>
            <div className="flex items-center gap-3">
              <Input
                type="number"
                min={1}
                step={50}
                placeholder="Unlimited"
                className="w-40"
                value={capValue}
                onChange={(e) => { setCapValue(e.target.value); setDirty(true); }}
                data-testid="input-rep-monthly-cap"
              />
              {capValue && (
                <span className="text-xs text-muted-foreground">
                  ≈ ${(parseInt(capValue || "0", 10) * 0.10).toFixed(0)}/mo max per rep
                </span>
              )}
            </div>
          </div>
        )}

        <Button
          size="sm"
          onClick={() => saveMutation.mutate()}
          disabled={!dirty || saveMutation.isPending}
          data-testid="button-save-rep-purchasing"
        >
          {saveMutation.isPending ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" />Saving…</> : "Save changes"}
        </Button>
      </CardContent>
    </Card>
  );
}

interface LeadRoiData {
  hasData: boolean;
  lifetime: { creditsUsed: number; ourCostUsd: number; creditsPurchased: number };
  thisMonth: { creditsUsed: number; ourCostUsd: number; creditsPurchased: number };
  byOperation: Array<{ operation: string; creditsUsed: number; ourCostUsd: number; unitsProduced: number }>;
  funnel: {
    totalLeads: number; convertedLeads: number; clientsInvoiced: number;
    totalInvoicedCents: number; paidInvoicedCents: number;
  };
}

const OP_LABELS: Record<string, string> = {
  lead_search: "Lead Search",
  email_finder: "Email Finder",
  qualification: "Qualification",
  enrichment: "Enrichment",
  screenshot_import: "Screenshot Import",
  ai_search: "AI Search",
};

function LeadRoiTab() {
  const { data, isLoading } = useQuery<LeadRoiData>({
    queryKey: ["/api/admin/lead-roi"],
    staleTime: 60_000,
  });

  if (isLoading) return (
    <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
  );

  if (!data?.hasData) {
    return (
      <Card>
        <CardContent className="py-16 flex flex-col items-center gap-4 text-center">
          <div className="w-14 h-14 rounded-full bg-muted flex items-center justify-center">
            <TrendingUp className="w-7 h-7 text-muted-foreground" />
          </div>
          <div>
            <p className="font-semibold text-base">No credit spend yet</p>
            <p className="text-sm text-muted-foreground mt-1 max-w-sm">
              Start tracking — spend some lead credits and your ROI will appear here. Costs are captured automatically as your team uses Lead Gen.
            </p>
          </div>
          {(data?.funnel?.totalLeads ?? 0) > 0 && (
            <div className="mt-4 p-4 bg-muted/50 rounded-lg text-sm">
              <p className="font-medium">You have {data!.funnel.totalLeads.toLocaleString()} leads already imported.</p>
              <p className="text-muted-foreground">Credit cost tracking begins from the next search or enrichment operation.</p>
            </div>
          )}
        </CardContent>
      </Card>
    );
  }

  const f = data.funnel;
  const conversionRate = f.totalLeads > 0 ? ((f.convertedLeads / f.totalLeads) * 100).toFixed(1) : "0";
  const invoiceRate = f.convertedLeads > 0 ? ((f.clientsInvoiced / f.convertedLeads) * 100).toFixed(1) : "0";
  const totalInvoiced = (f.totalInvoicedCents / 100).toLocaleString("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 0, maximumFractionDigits: 0 });
  const paidInvoiced = (f.paidInvoicedCents / 100).toLocaleString("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 0, maximumFractionDigits: 0 });

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-4 pb-3">
            <p className="text-xs text-muted-foreground">Credits used (lifetime)</p>
            <p className="text-2xl font-bold">{data.lifetime.creditsUsed.toLocaleString()}</p>
            <p className="text-xs text-muted-foreground mt-0.5">
              ≈ ${data.lifetime.ourCostUsd.toFixed(2)} cost
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3">
            <p className="text-xs text-muted-foreground">Credits used (this month)</p>
            <p className="text-2xl font-bold">{data.thisMonth.creditsUsed.toLocaleString()}</p>
            <p className="text-xs text-muted-foreground mt-0.5">
              ≈ ${data.thisMonth.ourCostUsd.toFixed(2)} cost
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3">
            <p className="text-xs text-muted-foreground">Credits purchased (lifetime)</p>
            <p className="text-2xl font-bold">{data.lifetime.creditsPurchased.toLocaleString()}</p>
            <p className="text-xs text-muted-foreground mt-0.5">
              at $0.10/cr ≈ ${(data.lifetime.creditsPurchased * 0.10).toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3">
            <p className="text-xs text-muted-foreground">Revenue from lead-sourced bookings</p>
            <p className="text-2xl font-bold">{paidInvoiced}</p>
            <p className="text-xs text-muted-foreground mt-0.5">{totalInvoiced} total invoiced</p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <BarChart2 className="w-4 h-4 text-primary" /> Credits by Operation
          </CardTitle>
          <CardDescription>How lead credits were spent across different operations</CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Operation</TableHead>
                <TableHead className="text-right">Credits used</TableHead>
                <TableHead className="text-right">Our cost</TableHead>
                <TableHead className="text-right">Units produced</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.byOperation.map((row) => (
                <TableRow key={row.operation}>
                  <TableCell className="font-medium">{OP_LABELS[row.operation] ?? row.operation}</TableCell>
                  <TableCell className="text-right font-mono">{row.creditsUsed.toLocaleString()}</TableCell>
                  <TableCell className="text-right font-mono">${row.ourCostUsd.toFixed(4)}</TableCell>
                  <TableCell className="text-right font-mono">{row.unitsProduced > 0 ? row.unitsProduced.toLocaleString() : "—"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-primary" /> Conversion Funnel
          </CardTitle>
          <CardDescription>How leads from the Lead Gen module progressed through your pipeline</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <div className="flex flex-col items-center p-3 rounded-lg bg-muted/50 min-w-[100px]">
              <span className="text-2xl font-bold">{f.totalLeads.toLocaleString()}</span>
              <span className="text-xs text-muted-foreground text-center">Total leads</span>
            </div>
            <ArrowRight className="w-4 h-4 text-muted-foreground shrink-0" />
            <div className="flex flex-col items-center p-3 rounded-lg bg-muted/50 min-w-[100px]">
              <span className="text-2xl font-bold">{f.convertedLeads.toLocaleString()}</span>
              <span className="text-xs text-muted-foreground text-center">Converted to client</span>
              <Badge variant="secondary" className="text-xs mt-1">{conversionRate}%</Badge>
            </div>
            <ArrowRight className="w-4 h-4 text-muted-foreground shrink-0" />
            <div className="flex flex-col items-center p-3 rounded-lg bg-muted/50 min-w-[100px]">
              <span className="text-2xl font-bold">{f.clientsInvoiced.toLocaleString()}</span>
              <span className="text-xs text-muted-foreground text-center">Clients invoiced</span>
              <Badge variant="secondary" className="text-xs mt-1">{invoiceRate}%</Badge>
            </div>
            <ArrowRight className="w-4 h-4 text-muted-foreground shrink-0" />
            <div className="flex flex-col items-center p-3 rounded-lg bg-green-50 dark:bg-green-950/20 border border-green-200 dark:border-green-900 min-w-[120px]">
              <span className="text-2xl font-bold text-green-700 dark:text-green-400">{paidInvoiced}</span>
              <span className="text-xs text-muted-foreground text-center">Paid</span>
            </div>
          </div>
          {f.totalLeads === 0 && (
            <div className="mt-4 flex items-start gap-2 p-3 rounded-lg bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-900">
              <AlertCircle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
              <p className="text-xs text-amber-700 dark:text-amber-400">
                No leads imported yet. Use Lead Gen → Import to source prospects, and your conversion funnel will appear here.
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default function AdminPage() {
  const [, navigate] = useLocation();
  const search = useSearch();
  const { toast } = useToast();
  const [selectedItem, setSelectedItem] = useState("users");
  const [usersAccessOpen, setUsersAccessOpen] = useState(false);
  const [invoicePoliciesOpen, setInvoicePoliciesOpen] = useState(false);
  const [invoicePoliciesInitialTab, setInvoicePoliciesInitialTab] = useState<
    "settings" | "cancellation" | "lateFees"
  >("settings");
  const [manageTalentOpen, setManageTalentOpen] = useState(false);
  const [workspaceSettingsOpen, setWorkspaceSettingsOpen] = useState(false);
  const [brandingSettingsOpen, setBrandingSettingsOpen] = useState(false);
  const [workspaceSettingsTab, setWorkspaceSettingsTab] = useState("clientTypes");
  const [profileSettingsOpen, setProfileSettingsOpen] = useState(false);
  const [teamActivityOpen, setTeamActivityOpen] = useState(false);
  const [communicationsSettingsOpen, setCommunicationsSettingsOpen] = useState(false);
  const [communicationsSettingsInitialTab, setCommunicationsSettingsInitialTab] = useState<
    "provider" | "permissions" | "invites" | "office-hours" | undefined
  >(undefined);
  const [formsTemplatesOpen, setFormsTemplatesOpen] = useState(false);
  const [integrationsOpen, setIntegrationsOpen] = useState(false);
  const [calendarSettingsOpen, setCalendarSettingsOpen] = useState(false);
  const [emailSettingsOpen, setEmailSettingsOpen] = useState(false);
  const [securityTrainingOpen, setSecurityTrainingOpen] = useState(false);
  const isMobile = useIsMobile();

  const { data: profile, isLoading, error } = useQuery<UserProfile>({
    queryKey: ["/api/profile"],
  });

  // Unread-voicemail badge on the Dialer nav item. Mirrors the unread count
  // shown inside the Dialer settings panel's Voicemail tab so reps get a
  // persistent at-a-glance signal without opening the panel. Re-fetches on
  // the same query key the Voicemail tab invalidates after listening.
  const { data: voicemails = [] } = useQuery<{ listenedAt: string | null }[]>({
    queryKey: ["/api/dialer/voicemails"],
  });
  const unreadVoicemailCount = voicemails.filter((v) => !v.listenedAt).length;

  // Handle deep-link query params: `?section=<key>` lets other pages
  // (e.g. the Catch Me Up budget-exceeded link) open a specific admin
  // panel directly. We strip the param after applying it so the URL
  // stays clean and refresh-friendly.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    let sectionParam = params.get("section");
    // Legacy aliases — old URLs from before the Invoice Policies
    // consolidation should still land on the unified panel and
    // pre-select the matching tab via handleMenuClick().
    const legacyAliases: Record<string, string> = {
      invoiceSettings: "invoiceSettings",
      lateFees: "lateFees",
      cancellationPolicies: "cancellationPolicies",
    };
    if (sectionParam && legacyAliases[sectionParam]) {
      handleMenuClick(sectionParam);
      sectionParam = "invoicePolicies";
    }
    // Cold-email audience policy was folded into the Email panel's Cold Email
    // area (Task #392). Old `?section=coldEmailPolicy` deep-links (including
    // the shared Cold Email Health card's link) now open the Email panel on
    // the Cold tab instead of a standalone section.
    if (sectionParam === "coldEmailPolicy") {
      sectionParam = "emailSettings";
      params.set("tab", "cold");
    }
    if (sectionParam === "branding") {
      setWorkspaceSettingsTab("branding");
      sectionParam = "workspaceSettings";
    }
    if (sectionParam && menuItems.some((m) => m.key === sectionParam)) {
      setSelectedItem(sectionParam);
      params.delete("section");
      const qs = params.toString();
      window.history.replaceState(
        {},
        "",
        window.location.pathname + (qs ? `?${qs}` : ""),
      );
    }

    // `?dialog=communications&tab=...` opens the Communications Settings
    // dialog directly on the requested tab. (Cold sending-domain setup moved
    // to the Email panel's Cold Email area — Task #392.)
    const dialogParam = params.get("dialog");
    const tabParam = params.get("tab");
    if (dialogParam === "communications") {
      const allowedTabs = ["provider", "permissions", "invites", "office-hours"] as const;
      type CommTab = typeof allowedTabs[number];
      const initialTab = (allowedTabs as readonly string[]).includes(tabParam || "")
        ? (tabParam as CommTab)
        : undefined;
      setCommunicationsSettingsInitialTab(initialTab);
      setCommunicationsSettingsOpen(true);
      params.delete("dialog");
      params.delete("tab");
      const qs2 = params.toString();
      window.history.replaceState(
        {},
        "",
        window.location.pathname + (qs2 ? `?${qs2}` : ""),
      );
    }
    // Re-run whenever the query string changes so deep-links work even when
    // we're already on /admin (wouter setLocation with a ?query doesn't remount
    // this page). The handlers strip the params they consume, so the follow-up
    // run settles to a no-op.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  // Handle OAuth callback params when returning from Google OAuth
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const calendarParam = params.get("calendar");
    const driveParam = params.get("drive");
    const reason = params.get("reason");

    if (calendarParam === "connected") {
      toast({ title: "Calendar Connected", description: "Google Calendar has been connected successfully!" });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/integrations/status"] });
      window.history.replaceState({}, "", window.location.pathname);
    } else if (calendarParam === "error") {
      toast({ title: "Calendar Connection Failed", description: reason || "Failed to connect Google Calendar", variant: "destructive" });
      window.history.replaceState({}, "", window.location.pathname);
    }

    if (driveParam === "connected") {
      toast({ title: "Google Drive Connected", description: "Google Drive has been connected successfully!" });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/integrations/status"] });
      window.history.replaceState({}, "", window.location.pathname);
    } else if (driveParam === "error") {
      toast({ title: "Drive Connection Failed", description: reason || "Failed to connect Google Drive", variant: "destructive" });
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, [toast]);

  const handleMenuClick = (key: string) => {
    setSelectedItem(key);
    if (!isMobile) return;
    switch (key) {
      case "users":
        setUsersAccessOpen(true);
        break;
      case "talent":
        setManageTalentOpen(true);
        break;
      case "invoicePolicies":
        setInvoicePoliciesInitialTab("settings");
        setInvoicePoliciesOpen(true);
        break;
      case "invoiceSettings":
        setInvoicePoliciesInitialTab("settings");
        setInvoicePoliciesOpen(true);
        break;
      case "cancellationPolicies":
        setInvoicePoliciesInitialTab("cancellation");
        setInvoicePoliciesOpen(true);
        break;
      case "lateFees":
        setInvoicePoliciesInitialTab("lateFees");
        setInvoicePoliciesOpen(true);
        break;
      case "workspaceSettings":
        setWorkspaceSettingsOpen(true);
        break;
      case "integrations":
        setIntegrationsOpen(true);
        break;
      case "bookingForms":
        setFormsTemplatesOpen(true);
        break;
      case "calendarSettings":
        setCalendarSettingsOpen(true);
        break;
      case "emailSettings":
        setEmailSettingsOpen(true);
        break;
      case "securityTraining":
        setSecurityTrainingOpen(true);
        break;
      case "security":
        // No dialog — handled inline in the panel below.
        break;
    }
  };

  const panelContent = useMemo(() => {
    switch (selectedItem) {
      case "users":
        return <UsersAccessDialog open={true} onOpenChange={() => {}} inPanel={true} />;
      case "lateFees":
        return <InvoicePoliciesPanel initialTab="lateFees" />;
      case "talent":
        return <ManageTalentDialog open={true} onOpenChange={() => {}} inPanel={true} />;
      case "integrations":
        return <IntegrationsDialog open={true} onOpenChange={() => {}} inPanel={true} />;
      case "workspaceSettings":
        return (
          <div className="p-4 sm:p-6 space-y-4 w-full overflow-auto">
            <Tabs value={workspaceSettingsTab} onValueChange={setWorkspaceSettingsTab}>
              <TabsList className="flex flex-wrap h-auto">
                <TabsTrigger value="clientTypes" data-testid="tab-workspace-settings-client-types">
                  <Building2 className="h-4 w-4 mr-1" /> Client Types
                </TabsTrigger>
                <TabsTrigger value="talentTypes" data-testid="tab-workspace-settings-talent-types">
                  <Users className="h-4 w-4 mr-1" /> Talent Types
                </TabsTrigger>
                <TabsTrigger value="collabRoles" data-testid="tab-workspace-settings-collab-roles">
                  <Users className="h-4 w-4 mr-1" /> Collaborator Roles
                </TabsTrigger>
                <TabsTrigger value="regions" data-testid="tab-workspace-settings-regions">
                  <MapPin className="h-4 w-4 mr-1" /> Regions
                </TabsTrigger>
                <TabsTrigger value="branding" data-testid="tab-workspace-settings-branding">
                  <Palette className="h-4 w-4 mr-1" /> Custom Branding
                </TabsTrigger>
                <TabsTrigger value="notifications" data-testid="tab-workspace-settings-notifications">
                  <Bell className="h-4 w-4 mr-1" /> Notifications
                </TabsTrigger>
              </TabsList>
              <TabsContent value="clientTypes" className="mt-4">
                <ManageClientTypesDialog open={true} onOpenChange={() => {}} inPanel={true} />
              </TabsContent>
              <TabsContent value="talentTypes" className="mt-4">
                <ManageTalentTypesDialog open={true} onOpenChange={() => {}} inPanel={true} />
              </TabsContent>
              <TabsContent value="collabRoles" className="mt-4">
                <CollaboratorRolesManager surface="workspace" inPanel={true} />
              </TabsContent>
              <TabsContent value="regions" className="mt-4">
                <RegionAccessSettingsDialog open={true} onOpenChange={() => {}} inPanel={true} />
              </TabsContent>
              <TabsContent value="branding" className="mt-4">
                <BrandingSettingsDialog open={true} onOpenChange={() => {}} inPanel={true} />
              </TabsContent>
              <TabsContent value="notifications" className="mt-4">
                <NotificationDefaultsCard />
              </TabsContent>
            </Tabs>
          </div>
        );
      case "invoiceSettings":
        return <InvoicePoliciesPanel initialTab="settings" />;
      case "invoicePolicies":
        return <InvoicePoliciesPanel initialTab="settings" />;
      case "teamActivity":
        return (
          <div className="flex-1 overflow-auto">
            <TeamActivityPage />
          </div>
        );
      case "bookingForms":
        return <FormsTemplatesDialog open={true} onOpenChange={() => {}} inPanel={true} />;
      case "calendarSettings":
        return <CalendarSettingsDialog open={true} onOpenChange={() => {}} inPanel={true} />;
      case "automations":
        return <EmailAutomationsPanel />;
      case "emailSettings":
        // Cold-email audience policy now lives inside the Email panel's
        // Cold Email area (Task #392), so it's no longer rendered standalone here.
        return (
          <div className="space-y-6">
            <EmailSettingsDialog open={true} onOpenChange={() => {}} inPanel={true} />
          </div>
        );
      case "cancellationPolicies":
        return <InvoicePoliciesPanel initialTab="cancellation" />;
      case "ai":
        return (
          <div className="p-4 sm:p-6 space-y-4 w-full overflow-auto">
            <Tabs defaultValue="general">
              <TabsList className="flex flex-wrap h-auto">
                <TabsTrigger value="general" data-testid="tab-ai-general">
                  <Sparkles className="h-4 w-4 mr-1" /> General
                </TabsTrigger>
                <TabsTrigger value="voice" data-testid="tab-ai-voice">
                  <Bot className="h-4 w-4 mr-1" /> Clarik AI
                </TabsTrigger>
                <TabsTrigger value="costRouting" data-testid="tab-ai-cost-routing">
                  <DollarSign className="h-4 w-4 mr-1" /> AI Credits
                </TabsTrigger>
                <TabsTrigger value="usage" data-testid="tab-ai-usage">
                  <BarChart2 className="h-4 w-4 mr-1" /> Usage Insights
                </TabsTrigger>
                <TabsTrigger value="teamProfiles" data-testid="tab-ai-team-profiles">
                  <Users className="h-4 w-4 mr-1" /> Team AI Profiles
                </TabsTrigger>
                <TabsTrigger value="lifeCoach" data-testid="tab-ai-life-coach">
                  <Heart className="h-4 w-4 mr-1" /> Life Coach
                </TabsTrigger>
              </TabsList>
              <TabsContent value="general" className="mt-4 space-y-6">
                <AISettingsCard />
              </TabsContent>
              <TabsContent value="costRouting" className="mt-4 space-y-6">
                <AiCostRoutingCard />
                <AiAutoReloadCard />
                <CreditDistributionCard creditType="ai" />
                <TalentBillingRollupCard />
              </TabsContent>
              <TabsContent value="voice" className="mt-4 space-y-6">
                <VoiceAssistantSettingsCard />
                <TourPreferencesCard />
              </TabsContent>
              <TabsContent value="usage" className="mt-4 space-y-6">
                <WorkspaceAiUsageInsights />
              </TabsContent>
              <TabsContent value="teamProfiles" className="mt-4">
                <WorkspaceTeamAiProfiles />
              </TabsContent>
              <TabsContent value="lifeCoach" className="mt-4 space-y-6">
                <AdminCoachPolicyCard />
              </TabsContent>
            </Tabs>
          </div>
        );
      case "sales":
        return (
          <div className="p-4 sm:p-6 space-y-4 w-full overflow-auto">
            <Tabs defaultValue="commissions">
              <TabsList className="flex flex-wrap h-auto">
                <TabsTrigger value="commissions" data-testid="tab-sales-commissions">
                  <Percent className="h-4 w-4 mr-1" /> Commissions
                </TabsTrigger>
                <TabsTrigger value="goals" data-testid="tab-sales-goals">
                  <Trophy className="h-4 w-4 mr-1" /> Goals
                </TabsTrigger>
                <TabsTrigger value="approvals" data-testid="tab-sales-approvals">
                  <ClipboardCheck className="h-4 w-4 mr-1" /> Approvals
                </TabsTrigger>
              </TabsList>
              <TabsContent value="commissions" className="mt-4">
                <ManageCommissionsDialog open={true} onOpenChange={() => {}} inPanel={true} />
              </TabsContent>
              <TabsContent value="goals" className="mt-4 space-y-6">
                <GamificationSettingsDialog open={true} onOpenChange={() => {}} inPanel={true} />
              </TabsContent>
              <TabsContent value="approvals" className="mt-4 space-y-6">
                <ApprovalPoliciesPanel />
                <ApprovalsInbox scope="admin" />
                <AdminSeatRequestsPanel />
              </TabsContent>
            </Tabs>
          </div>
        );
      case "dialer":
        return (
          <div className="p-4 sm:p-6 space-y-4 w-full overflow-auto">
            <AdminDialerSettingsTab />
          </div>
        );
      case "talentPortal":
        return <TalentPortalSettings />;
      case "leadSettings":
        return (
          <div className="p-4 sm:p-6 space-y-4 w-full overflow-auto">
            <Tabs defaultValue="settings">
              <TabsList className="flex flex-wrap h-auto">
                <TabsTrigger value="settings" data-testid="tab-lead-settings-general">
                  <MapPin className="h-4 w-4 mr-1" /> Settings
                </TabsTrigger>
                <TabsTrigger value="credits" data-testid="tab-lead-settings-credits">
                  <Coins className="h-4 w-4 mr-1" /> Lead Credits
                </TabsTrigger>
                <TabsTrigger value="roi" data-testid="tab-lead-settings-roi">
                  <TrendingUp className="h-4 w-4 mr-1" /> ROI
                </TabsTrigger>
              </TabsList>
              <TabsContent value="settings" className="mt-4 space-y-6">
                <AdminLeadSettingsCard />
              </TabsContent>
              <TabsContent value="credits" className="mt-4 space-y-6">
                <LeadAutoTopUpCard />
                <RepPurchasingCard />
                <CreditDistributionCard creditType="lead" />
              </TabsContent>
              <TabsContent value="roi" className="mt-4">
                <LeadRoiTab />
              </TabsContent>
            </Tabs>
          </div>
        );
      case "security":
        return (
          <div className="p-4 sm:p-6 space-y-4 w-full overflow-auto">
            <Tabs defaultValue="settings">
              <TabsList className="flex flex-wrap h-auto">
                <TabsTrigger value="settings" data-testid="tab-security-settings">
                  <ShieldCheck className="h-4 w-4 mr-1" /> Settings
                </TabsTrigger>
                <TabsTrigger value="events" data-testid="tab-security-events">
                  <ShieldAlert className="h-4 w-4 mr-1" /> Security Logs
                </TabsTrigger>
                <TabsTrigger value="documentSecurity" data-testid="tab-security-document-security">
                  <FileText className="h-4 w-4 mr-1" /> Document Security
                </TabsTrigger>
              </TabsList>
              <TabsContent value="settings" className="mt-4">
                <AdminSecuritySettingsCard />
              </TabsContent>
              <TabsContent value="events" className="mt-4">
                <SecurityEventsPanel />
              </TabsContent>
              <TabsContent value="documentSecurity" className="mt-4">
                <DocumentSecurityPanel />
              </TabsContent>
            </Tabs>
          </div>
        );
      default:
        return (
          <div className="flex-1 flex items-center justify-center text-muted-foreground p-6">
            <p>Select a menu item to configure settings</p>
          </div>
        );
    }
  }, [selectedItem, workspaceSettingsTab]);

  if (isLoading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-center space-y-2">
          <p className="text-destructive">Failed to load profile</p>
          <p className="text-sm text-muted-foreground">Please try refreshing the page</p>
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-center space-y-2">
          <p className="text-destructive">No profile found</p>
          <p className="text-sm text-muted-foreground">Please log out and log back in</p>
          <Button variant="outline" onClick={() => navigate("/")}>
            Go to Home
          </Button>
        </div>
      </div>
    );
  }

  if (profile.role?.toLowerCase() !== "admin") {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-center space-y-2">
          <p className="text-destructive">Access Denied</p>
          <p className="text-sm text-muted-foreground">You need admin permissions to access this page</p>
          <Button variant="outline" onClick={() => navigate("/")}>
            Go to Dashboard
          </Button>
        </div>
      </div>
    );
  }

  const currentItem = menuItems.find(item => item.key === selectedItem);

  const renderMenuItem = (item: typeof menuItems[number]) => {
    const Icon = item.icon;
    const isSelected = selectedItem === item.key;
    return (
      <div key={item.key} className="flex items-center gap-1">
        <button
          onClick={() => handleMenuClick(item.key)}
          className={`flex-1 flex items-center gap-2.5 px-3 py-2 rounded-md text-left transition-colors ${
            isSelected
              ? "bg-primary text-primary-foreground"
              : "hover-elevate text-foreground"
          }`}
          data-testid={`button-menu-${item.key}`}
        >
          <Icon className="w-4 h-4 flex-shrink-0" />
          <span className="text-sm font-medium truncate">{item.label}</span>
          {item.key === "dialer" && unreadVoicemailCount > 0 && (
            <Badge
              variant={isSelected ? "secondary" : "default"}
              className="ml-auto h-5 min-w-[1.25rem] justify-center rounded-full px-1.5 text-xs"
              data-testid="badge-dialer-unread-voicemails"
            >
              {unreadVoicemailCount}
            </Badge>
          )}
        </button>
        <TutorialHint id={`admin.${item.key}`} />
      </div>
    );
  };

  return (
    <div className="flex-1 flex overflow-hidden">
      <div className="w-56 border-r bg-muted/30 flex flex-col shrink-0">
        <div className="px-4 py-4 border-b">
          <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">Settings</h2>
        </div>
        <ScrollArea className="flex-1">
          <div className="p-3 space-y-4">
            {menuGroups.map((group) => (
              <div key={group.group} className="space-y-0.5">
                <h3
                  className="px-3 pb-1 text-xs font-semibold text-muted-foreground uppercase tracking-wide"
                  data-testid={`text-settings-group-${group.group.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                >
                  {group.group}
                </h3>
                {group.items.map(renderMenuItem)}
              </div>
            ))}
          </div>
        </ScrollArea>
      </div>

      <div className="flex-1 flex flex-col overflow-hidden">
        <div className="border-b px-3 sm:px-6 py-3 sm:py-4 flex items-center justify-between gap-4 shrink-0">
          <div>
            <h1 className="text-xl font-semibold" data-testid="text-page-title">
              {currentItem?.label || "Administration"}
            </h1>
            <p className="text-sm text-muted-foreground">
              {currentItem?.description || "Manage users, system settings, and configurations"}
            </p>
          </div>
        </div>

        <div className="hidden md:flex flex-1 overflow-hidden">
          {panelContent}
        </div>

        <div className="md:hidden flex-1 flex items-center justify-center text-muted-foreground p-6">
          <p className="text-sm">Tap a menu item on the left to open its settings.</p>
        </div>
      </div>

      {isMobile && (
        <>
          <UsersAccessDialog open={usersAccessOpen} onOpenChange={setUsersAccessOpen} />
          <ManageTalentDialog open={manageTalentOpen} onOpenChange={setManageTalentOpen} />
          <Dialog open={workspaceSettingsOpen} onOpenChange={setWorkspaceSettingsOpen}>
            <DialogContent className="max-w-[95vw] max-h-[90vh] overflow-y-auto p-0">
              <div className="p-4 space-y-4">
                <h2 className="text-lg font-semibold">Workspace Settings</h2>
                <Tabs value={workspaceSettingsTab} onValueChange={setWorkspaceSettingsTab}>
                  <TabsList className="flex flex-wrap h-auto">
                    <TabsTrigger value="clientTypes" data-testid="tab-workspace-settings-client-types-mobile">
                      <Building2 className="h-4 w-4 mr-1" /> Client Types
                    </TabsTrigger>
                    <TabsTrigger value="talentTypes" data-testid="tab-workspace-settings-talent-types-mobile">
                      <Users className="h-4 w-4 mr-1" /> Talent Types
                    </TabsTrigger>
                    <TabsTrigger value="regions" data-testid="tab-workspace-settings-regions-mobile">
                      <MapPin className="h-4 w-4 mr-1" /> Regions
                    </TabsTrigger>
                    <TabsTrigger value="branding" data-testid="tab-workspace-settings-branding-mobile">
                      <Palette className="h-4 w-4 mr-1" /> Custom Branding
                    </TabsTrigger>
                    <TabsTrigger value="notifications" data-testid="tab-workspace-settings-notifications-mobile">
                      <Bell className="h-4 w-4 mr-1" /> Notifications
                    </TabsTrigger>
                  </TabsList>
                  <TabsContent value="clientTypes" className="mt-4">
                    <ManageClientTypesDialog open={true} onOpenChange={() => {}} inPanel={true} />
                  </TabsContent>
                  <TabsContent value="talentTypes" className="mt-4">
                    <ManageTalentTypesDialog open={true} onOpenChange={() => {}} inPanel={true} />
                  </TabsContent>
                  <TabsContent value="regions" className="mt-4">
                    <RegionAccessSettingsDialog open={true} onOpenChange={() => {}} inPanel={true} />
                  </TabsContent>
                  <TabsContent value="branding" className="mt-4">
                    <BrandingSettingsDialog open={true} onOpenChange={() => {}} inPanel={true} />
                  </TabsContent>
                  <TabsContent value="notifications" className="mt-4">
                    <NotificationDefaultsCard />
                  </TabsContent>
                </Tabs>
              </div>
            </DialogContent>
          </Dialog>
          <BrandingSettingsDialog open={brandingSettingsOpen} onOpenChange={setBrandingSettingsOpen} />
          <FormsTemplatesDialog open={formsTemplatesOpen} onOpenChange={setFormsTemplatesOpen} />
          <IntegrationsDialog open={integrationsOpen} onOpenChange={setIntegrationsOpen} />
          <CalendarSettingsDialog open={calendarSettingsOpen} onOpenChange={setCalendarSettingsOpen} />
          <EmailSettingsDialog open={emailSettingsOpen} onOpenChange={setEmailSettingsOpen} />
          <Dialog open={invoicePoliciesOpen} onOpenChange={setInvoicePoliciesOpen}>
            <DialogContent className="max-w-[95vw] max-h-[90vh] overflow-y-auto p-0">
              <InvoicePoliciesPanel initialTab={invoicePoliciesInitialTab} />
            </DialogContent>
          </Dialog>
        </>
      )}

      <ProfileSettingsDialog open={profileSettingsOpen} onOpenChange={setProfileSettingsOpen} />
      <TeamActivityDialog open={teamActivityOpen} onOpenChange={setTeamActivityOpen} />
      <CommunicationsSettingsDialog
        open={communicationsSettingsOpen}
        onOpenChange={(next) => {
          setCommunicationsSettingsOpen(next);
          // Clear the deep-linked tab on close so a subsequent
          // manual open returns to the dialog's natural default.
          if (!next) setCommunicationsSettingsInitialTab(undefined);
        }}
        initialTab={communicationsSettingsInitialTab}
      />
    </div>
  );
}
